import { notifications } from '@mantine/notifications';
import { MantineTheme } from '@mantine/core';
import axios from 'axios';

const successStyles = (theme: MantineTheme) => ({
  root: {
    backgroundColor: theme.black,
    borderColor: theme.colors.green[6],
    borderLeftWidth: 4,
    '&::before': { backgroundColor: theme.colors.green[6] },
  },
  title: { color: theme.white },
  description: { color: theme.colors.gray[4] },
  closeButton: {
    color: theme.colors.gray[4],
    '&:hover': { backgroundColor: theme.colors.dark[6] },
  },
});

const errorStyles = (theme: MantineTheme) => ({
  root: {
    backgroundColor: theme.black,
    borderColor: theme.colors.red[6],
    borderLeftWidth: 4,
    '&::before': { backgroundColor: theme.colors.red[6] },
  },
  title: { color: theme.white },
  description: { color: theme.colors.gray[4] },
  closeButton: {
    color: theme.colors.gray[4],
    '&:hover': { backgroundColor: theme.colors.dark[6] },
  },
});

/**
 * Extract a user-friendly error message from an error object
 */
export const extractErrorMessage = (error: unknown, fallbackMessage: string): string => {
  if (axios.isAxiosError(error)) {
    // Try to extract message from response data
    const responseData = error.response?.data;
    
    if (responseData?.detail && typeof responseData.detail === 'string') {
      const detail = responseData.detail;
      
      // Truncate very long messages (keep under ~100 chars for notifications)
      if (detail.length > 100) {
        return detail.substring(0, 97) + '...';
      }
      
      // Return the API message if it's a reasonable length
      return detail;
    }
    
    // Try other common API error fields
    if (responseData?.message && typeof responseData.message === 'string') {
      const message = responseData.message;
      return message.length > 100 ? message.substring(0, 97) + '...' : message;
    }
    
    if (responseData?.error && typeof responseData.error === 'string') {
      const errorMsg = responseData.error;
      return errorMsg.length > 100 ? errorMsg.substring(0, 97) + '...' : errorMsg;
    }
    
    // For network errors or other axios errors
    if (error.message && !error.response) {
      return 'Network error. Please check your connection and try again.';
    }
  }
  
  // Fallback to provided message
  return fallbackMessage;
};

export const showSuccessNotification = (message: string, title: string = 'Success') => {
  notifications.show({
    title,
    message,
    color: 'green',
    autoClose: 3000,
    withCloseButton: true,
    styles: successStyles,
  });
};

export const showErrorNotification = (message: string, title: string = 'Error') => {
  notifications.show({
    title,
    message,
    color: 'red',
    autoClose: false, // Persist until manually dismissed - errors need user attention
    withCloseButton: true,
    styles: errorStyles,
  });
};

/**
 * Show an error notification with automatic API error extraction
 */
export const showApiErrorNotification = (error: unknown, fallbackMessage: string, title: string = 'Error') => {
  const message = extractErrorMessage(error, fallbackMessage);
  showErrorNotification(message, title);
}; 